import { useState, useMemo, useCallback, useEffect } from "react";
import { PERMISSION_CATEGORIES, type PermissionCategories, type RolesAndPermissionsData } from "./types";
import { useRoles } from "./useRoles";
import { usePermissions } from "./usePermissions";
import { useUpdateRolePermissions } from "./useUpdateRolePermissions";
import {
  categorizePermissions,
  mapPermissionDataToRoleInput,
  buildInitialPermissionMatrix,
  normalizePermissionMatrix
} from "./permissionHelpers";

interface UsePermissionsMatrixReturn {
  rolesAndPermissionsData: RolesAndPermissionsData | null;
  permissionData: Record<string, Record<string, boolean>>;
  categorizedPermissions: Record<string, string[]>;
  isLoading: boolean;
  error: Error | null;
  handlePermissionDataChange: (newData: Record<string, Record<string, boolean>>) => Promise<boolean>;
}

interface UsePermissionsMatrixOptions {
  categories?: PermissionCategories;
  normalizeParentPermissions?: boolean;
}

export const usePermissionsMatrix = ({
  categories = PERMISSION_CATEGORIES,
  normalizeParentPermissions = false,
}: UsePermissionsMatrixOptions = {}): UsePermissionsMatrixReturn => {
  const [permissionData, setPermissionData] = useState<Record<string, Record<string, boolean>>>({});

  const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useRoles();
  const { data: permissionsData, isLoading: permissionsLoading, error: permissionsError } = usePermissions();
  const updateRolePermissionsMutation = useUpdateRolePermissions();

  const rolesAndPermissionsData = useMemo<RolesAndPermissionsData | null>(() => {
    if (rolesData && permissionsData) {
      const permissionMatrix: Record<string, string[]> = {};
      permissionsData.permissions.forEach(permission => {
        permissionMatrix[permission] = rolesData.roles
          .filter(role => role.permissions.includes(permission))
          .map(role => role.name);
      });

      return {
        roles: rolesData.roles,
        allPermissions: permissionsData.permissions,
        permissionMatrix,
      };
    }
    return null;
  }, [rolesData, permissionsData]);

  // Build the permission matrix whenever data changes
  const buildPermissionMatrix = useCallback(() => {
    if (rolesAndPermissionsData) {
      const { roles, allPermissions } = rolesAndPermissionsData;
      const matrix = buildInitialPermissionMatrix(roles, allPermissions);
      setPermissionData(matrix);
    }
  }, [rolesAndPermissionsData]);

  // Rebuild matrix when the underlying data changes
  useEffect(() => {
    buildPermissionMatrix();
  }, [buildPermissionMatrix]);

  // Categorise permissions for UI display
  const categorizedPermissions = useMemo(() => {
    return permissionsData ? categorizePermissions(permissionsData.permissions, categories) : {};
  }, [categories, permissionsData]);

  const handlePermissionDataChange = useCallback(async (newData: Record<string, Record<string, boolean>>) => {
    const previousData = permissionData;
    const normalizedData = normalizeParentPermissions ? normalizePermissionMatrix(newData) : newData;
    const roleInput = mapPermissionDataToRoleInput(normalizedData);

    // Update the UI immediately
    setPermissionData(normalizedData);

    try {
      await updateRolePermissionsMutation.mutateAsync({ roles: roleInput });
      return true;
    } catch (err) {
      // Rollback to previous state on error
      setPermissionData(previousData);
      throw err;
    }
  }, [updateRolePermissionsMutation, permissionData, normalizeParentPermissions]);

  return {
    rolesAndPermissionsData,
    permissionData,
    categorizedPermissions,
    isLoading: rolesLoading || permissionsLoading,
    error: (rolesError || permissionsError || updateRolePermissionsMutation.error) as Error | null,
    handlePermissionDataChange,
  };
};
